import type {
  DestinationSyncSettings,
  DecisionEngineChannelType,
} from "@hightouch/lib/customer-data/decision-engine/types";
import type {
  DecisionEngineDestinationDefinition,
  SyncSpec,
} from "./definition";
import { accDefinition } from "./adobe-campaign-classic";
import { nativeDeliveryDefinition } from "./native-delivery";
import { ajoDefinition } from "./adobe-experience-platform";
import { airshipDefinition } from "./airship";
import { attentiveDefinition } from "./attentive";
import { brazeCanvasDefinition, brazeTriggerDefinition } from "./braze";
import { cordialDefinition } from "./cordial";
import { eloquaCdoDefinition, eloquaTriggerDefinition } from "./eloqua";
import { iterableDefinition } from "./iterable";
import { klaviyoDefinition } from "./klaviyo";
import { marketoDefinition } from "./marketo";
import { moengageDefinition } from "./moengage";
import { mongodbDefinition } from "./mongodb";
import { oneSignalDefinition } from "./one-signal";
import { personalizationApiDefinition } from "./personalization-api";
import {
  sfmcDataExtensionsDefinition,
  sfmcJourneyDefinition,
  sfmcTriggerDefinition,
} from "./sfmc";

const DESTINATION_REGISTRY: Record<
  string,
  DecisionEngineDestinationDefinition
> = {
  // Trigger
  iterable: iterableDefinition,
  "iterable:template": iterableDefinition,
  "iterable:campaign": iterableDefinition,
  braze: brazeTriggerDefinition,
  "braze:trigger": brazeTriggerDefinition,
  "braze:canvas": brazeCanvasDefinition,
  sfmc: sfmcTriggerDefinition,
  "sfmc:trigger": sfmcTriggerDefinition,
  eloqua: eloquaTriggerDefinition,
  "eloqua:trigger": eloquaTriggerDefinition,

  cordial: cordialDefinition,
  marketo: marketoDefinition,
  onesignal: oneSignalDefinition,
  "adobe-campaign-classic": accDefinition,
  airship: airshipDefinition,

  // Event
  klaviyo: klaviyoDefinition,
  attentive: attentiveDefinition,
  moengage: moengageDefinition,
  "adobe-experience-platform": ajoDefinition,
  "sfmc:journeyEntryEvent": sfmcJourneyDefinition,
  "eloqua:customDataObject": eloquaCdoDefinition,

  // Batch
  "sfmc:dataExtensions": sfmcDataExtensionsDefinition,

  // Onsite
  "hightouch-personalization-api": personalizationApiDefinition,

  // Native delivery: campaigns owned by this workspace's CEP channels
  "hightouch-native-delivery": nativeDeliveryDefinition,

  // Custom passthrough — dumps the AID row as-is; downstream system handles transforms.
  mongodb: mongodbDefinition,
};

function definitionKey(
  destinationType: string,
  settings?: DestinationSyncSettings,
): string {
  return settings?.type
    ? `${destinationType}:${settings.type}`
    : destinationType;
}

/**
 * Look up a destination definition by type and optional channel config.
 *
 * Tries `${destinationType}:${settings.type}` first (e.g. "sfmc:trigger"),
 * then falls back to `${destinationType}` (e.g. "sfmc" -> trigger default).
 * Returns undefined if no definition is found.
 */
function findDestinationDefinition(
  destinationType: string,
  settings?: DestinationSyncSettings,
): DecisionEngineDestinationDefinition | undefined {
  return (
    DESTINATION_REGISTRY[definitionKey(destinationType, settings)] ??
    DESTINATION_REGISTRY[destinationType]
  );
}

/**
 * Look up a destination definition by type and optional channel config.
 * Throws if no definition is found.
 */
export function getDestinationDefinition(
  destinationType: string,
  settings?: DestinationSyncSettings,
): DecisionEngineDestinationDefinition {
  const definition = findDestinationDefinition(destinationType, settings);
  if (!definition) {
    throw new Error(
      `Unknown decision engine destination type: ${definitionKey(destinationType, settings)}`,
    );
  }
  return definition;
}

/**
 * Generate a destination's sync config from a spec, then apply the spec's
 * overrides: `syncConfigOverride` is spread last, `syncConfigAppend` extends
 * matching array properties or creates them.
 */
export function generateSyncConfigFromSpec(
  destinationType: string,
  spec: SyncSpec,
): Record<string, unknown> {
  const definition = getDestinationDefinition(destinationType, spec.settings);
  if (definition.producers && !definition.producers.includes(spec.producer)) {
    throw new Error(
      `${definitionKey(destinationType, spec.settings)} does not generate ${spec.producer} syncs`,
    );
  }
  const syncConfig: Record<string, unknown> = {
    ...definition.generateSyncConfig(spec),
    ...(spec.syncConfigOverride ?? {}),
  };
  for (const [key, value] of Object.entries(spec.syncConfigAppend ?? {})) {
    if (!Array.isArray(value)) continue;
    const existing = syncConfig[key];
    if (existing == null) {
      syncConfig[key] = [...value];
    } else if (Array.isArray(existing)) {
      syncConfig[key] = [...existing, ...value];
    }
  }
  return syncConfig;
}

/**
 * Whether a channel's sync is enrollment-shaped (see
 * `DecisionEngineDestinationDefinition.enrollmentChannelTypes`).
 */
export function isEnrollmentChannel(
  destinationType: string,
  channelType: string,
  settings?: DestinationSyncSettings,
): boolean {
  return (
    findDestinationDefinition(
      destinationType,
      settings,
    )?.enrollmentChannelTypes?.includes(
      channelType as DecisionEngineChannelType,
    ) ?? false
  );
}
